import React, { FC, HTMLAttributes, useEffect, useState } from "react";
import TableActionBar from "./table-action-bar";
import EditIcon from "@mui/icons-material/Edit";
import InfiniteScroll from "react-infinite-scroll-component";
import { isMobileDevice } from "../../../helpers/device-info-helper";
import TransactionInfoModal from "./transaction-info-modal";
import { cloneDeep } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
  getCashManagementList,
  getCurrentTransactionList,
  updateCurrentTransactionData,
} from "../../../redux/slices";
import { RootState } from "../../../redux/store";
import moment from "moment";
import usePrevious from "../../../hooks/usePrevious";
import {
  CASH_TABLE_HEADER,
  TRANSACTION_TYPE,
} from "./cash-managment-constants";
import { isUserCashier } from "../../../helpers/isUserCashier";
import { currency } from "../../../helpers/common-helper";

interface Props {
  showAddTransactionView: (...args: any[]) => void;
  showEditTransaction: (...args: any[]) => void;
  trxnIdForToast: any;
  resetTrxnIdForToast: (...args: any[]) => void;
}
const currencySymbol = currency();
const CashManagementTable = ({
  showAddTransactionView,
  showEditTransaction,
  trxnIdForToast = "",
  resetTrxnIdForToast,
}: Props) => {
  const tableHeaderArr = isUserCashier() ? [CASH_TABLE_HEADER[0]] : CASH_TABLE_HEADER;
  const [selectedBranch, setSelectedBranch] = useState<any>({}),
        [selectedTransactionType, setSelectedTransactionType] = useState<any>(tableHeaderArr[0].val),
        [hasMoreData, setHasMoreData] = useState<boolean>(true),
        [transactionList, setTransactionList] = useState<any>([]),
        [showTransactionInfoModal, setShowTransactionInfoModal] = useState<boolean>(false),
        [transactionConfig, setTransactionConfig] = useState<any>({branchId: "",}),
        dispatch = useDispatch();
  let prevType = usePrevious(selectedTransactionType);

  const { transactionsListData, currentTransactionDetails } = useSelector(
    (state: RootState) => ({
      transactionsListData: state.accountingReducer.cashManagementData,
      currentTransactionDetails:
        state.accountingReducer.currentTransactionDetails,
    })
  );

  useEffect(() => {
    setSelectedTransactionType(tableHeaderArr[0].val);
    dispatch(updateCurrentTransactionData({}));
  }, []);

  useEffect(() => {
    if (transactionConfig?.branchId && !isUserCashier()) {
      fetchTableData(transactionConfig, false, false);
    }
  }, [transactionConfig?.branchId]);

  useEffect(() => {
    if(isUserCashier())
    {
      fetchCurrentTableData()
    }
    else if(prevType && selectedTransactionType !== prevType) {
      fetchTableData(transactionConfig, false, false);
    }
  }, [selectedTransactionType]);

  useEffect(() => {
    if (trxnIdForToast) {
      setShowTransactionInfoModal(!showTransactionInfoModal);
      resetTrxnIdForToast();
    }
  }, [trxnIdForToast]);

  const fetchCurrentTableData = () => {
    const callback = (isSuccess = true, response : any) => {
      if(isSuccess) {
         setTransactionList([...(response?.localTransactions || [])?.reverse(), ...response?.transactions])
         setHasMoreData(false)
      }
    }
    dispatch(getCurrentTransactionList({callback}))
 }

  const selectTransaction = (value: any) => {
    setSelectedTransactionType(value);
  };

  const openEditView = (event: any, obj: any) => {
    event.stopPropagation();
    showEditTransaction();
    dispatch(updateCurrentTransactionData(obj));
  };

  const showEditBtn = (trxnType: any) => {
    if (
      trxnType === "Transfer In" ||
      trxnType === "Transfer Out" ||
      trxnType === "Expense"
    ) {
      return true;
    }
    return false;
  };

  const getMbView = (obj:any) => {
    return (
      <div
        className="transaction-info-cnt-mb flex flex-col border-b border-[#ECECEC] last:border-0 px-24 py-15"
        onClick={() => showTransactionModal(obj)}
      >
        <div className="flex justify-between w-full pb-8">
          <div className="amt-info-cnt">
            <span className="amt-type text-14 text-black font-medium inline-block pr-5 border-r border-black py-[2px]">
              {obj.mode}
            </span>
            <span className="amt-cnt text-14 text-black font-medium inline-block pl-5">
              {obj.amount}
            </span>
          </div>
          {showEditBtn(obj.transactionType) && (
            <div className="edit-btn" onClick={(e) => openEditView(e, obj)}>
              <span className="text-14 font-medium text-[#397EF5] inline-block pr-5">
                Edit
              </span>
              <EditIcon sx={{ color: "#397EF5", fontSize: "15px" }} />
            </div>
          )}
        </div>
        <div className="flex justify-between w-full items-center">
          <div className="w-6/12">
            <div className="amt-status text-14 text-black font-medium">
              {obj.transactionType}
            </div>
            <div className="person-date-cnt text-14 text-black opacity-50 font-normal inline-block">
              {obj.updatedBy?.userName?.firstName &&
                `By ${obj.updatedBy?.userName?.firstName}`}{" "}
              {obj.updatedBy?.date
                ? `on ${moment(obj.updatedBy.date).format("DD-MMM-YYYY")}`
                : ""}{" "}
            </div>
          </div>
          <div className="branch-name text-14 text-black font-normal opacity-50 py-10">
            {selectedBranch.branchName}
          </div>
        </div>
      </div>
    )
  }

  const getDesktopView = (obj:any) => {
    return (
      <div
        className="transaction-info-cnt grid grid-cols-[1fr_1fr_1fr_2fr_1fr] grid-rows-[70px] items-center text-center border-b border-[#ECECEC] last:border-0 cursor-pointer hover:bg-[#F6F8FC]"
        onClick={() => showTransactionModal(obj)}
      >
        <div className="amt-cnt text-14 text-black font-semibold border-r border-[#ECECEC] py-10">
          {currencySymbol}{obj.amount}
        </div>
        <div className="amt-status text-14 text-black font-medium border-r border-[#ECECEC] py-10">
          {obj.transactionType}
        </div>
        <div className="branch-name text-14 text-black font-medium border-r border-[#ECECEC] py-10">
          {selectedBranch.branchName}
        </div>
        <div className="amt-info-cnt text-left pl-30 ">
          <div className="amt-type text-14 text-black font-normal inline-block pr-15">
            {obj.mode}
          </div>
          <div className="person-date-cnt text-14 text-black opacity-50 font-normal inline-block">
            {obj.updatedBy?.date
              ? `${moment(obj.updatedBy.date).format("DD-MMM-YYYY")} - `
              : ""}{" "}
            {obj.updatedBy?.userName?.firstName &&
              `by ${obj.updatedBy?.userName?.firstName}`}
          </div>
        </div>
        <div className="edit-btn text-right pr-40">
          {showEditBtn(obj.transactionType) && (
            <EditIcon
              sx={{ color: "#397EF5", cursor: "pointer" }}
              onClick={(e) => openEditView(e, obj)}
            />
          )}
        </div>
      </div>
    )
  }

  const getTransactionRowView = (obj: any) => {
    return (
      <>
        {isMobileDevice() ? (
          <>
            {getMbView(obj)}
          </>
        ) : (
          <>
            {getDesktopView(obj)}
          </>
        )}
      </>
    );
  };

  const showTransactionModal = (obj: any) => {
    setShowTransactionInfoModal(!showTransactionInfoModal);
    dispatch(updateCurrentTransactionData(obj));
  };

  const fetchTableData = (
    dataParams: any,
    addPagination: boolean,
    appendList: boolean
  ) => {
    const callback = (isSuccess: boolean, response: any) => {
      let dataList = response.results;
      if (isSuccess) {
        if (dataList?.length) {
          if (appendList) {
            setTransactionList([...transactionList, ...dataList]);
          } else {
            setTransactionList(dataList);
          }
          if (dataList?.length < 20) {
            setHasMoreData(false);
          }
        } else {
          setTransactionList([]);
          setHasMoreData(false);
        }
      } else {
        setHasMoreData(false);
      }
    };
    let dataToPost: any = cloneDeep(dataParams);
    dataToPost["pageSize"] = 20;
    if (selectedTransactionType !== "all") {
      dataToPost["transactionType"] = selectedTransactionType;
    }
    if (addPagination && transactionsListData.lastKey) {
      dataToPost["lastKey"] = transactionsListData.lastKey;
    }
    dispatch(getCashManagementList({ dataToPost, callback }));
  };

  const fetchMoreData = () => {
    setTimeout(() => {
      fetchTableData(transactionConfig, true, true);
    }, 1000);
  };

  const getLoadMore = () => {
    return (
      <div className="text-center p-15">
        <span className="text-16 text-black">Loading more....</span>
      </div>
    );
  };

  const updateBranchSelection = (branchInfo: any) => {
    let config = cloneDeep(transactionConfig);
    config.branchId = branchInfo.branchId;
    setTransactionConfig(config);
    setSelectedBranch(branchInfo);
    !hasMoreData && setHasMoreData(true);
  };

  const refreshTableData = () => {
    if(isUserCashier())
    {
      fetchCurrentTableData()
    }
    else {
      fetchTableData(transactionConfig, false, false);
      !hasMoreData && setHasMoreData(true);
    }
  };

  const closeInfoModal = () => {
    setShowTransactionInfoModal(false);
    dispatch(updateCurrentTransactionData({}));
  };

  const showAddView = () => {
    if (selectedTransactionType === TRANSACTION_TYPE.all)
      showAddTransactionView("1");
    if (selectedTransactionType === TRANSACTION_TYPE.transferIn)
      showAddTransactionView("1");
    if (selectedTransactionType === TRANSACTION_TYPE.transferOut)
      showAddTransactionView("2");
    if (selectedTransactionType === TRANSACTION_TYPE.expense)
      showAddTransactionView("3");
  };

  return (
    <div className="cash-management-table-container mt-16 border border-[#DDDDDD] rounded-[10px] flex-1 sm:border-0 relative">
      <TableActionBar
        updateSelectedBranch={updateBranchSelection}
        showAddTransactionView={showAddView}
        refershData={refreshTableData}
      />
      <div className="cash-transactions-table-container">
        <div className="table-header-cnt flex items-center border-t border-b border-[#EDEDED] mb-10 sm:whitespace-nowrap sm:overflow-x-scroll sm:overflow-y-hidden">
          {tableHeaderArr.map((obj: any) => (
            <div
              className={`header-name px-30 py-14 cursor-pointer hover:bg-[#F6F8FC] mb-[-1px] sm:px-15 ${
                obj.val === selectedTransactionType
                  ? "border-b-2 border-[#397EF5]"
                  : ""
              }`}
              key={obj.id}
              onClick={() => selectTransaction(obj.val)}
            >
              <span
                className={`text-18 font-inter ${
                  obj.val === selectedTransactionType
                    ? "font-medium text-[#397EF5] opacity-100"
                    : "opacity-50 text-black font-normal"
                }`}
              >
                {obj.name}
              </span>
            </div>
          ))}
        </div>
        <div className="table-list-cnt sm:bg-white">
          {!transactionList.length ? (
            <div className="text-center absolute top-1/2 left-1/2 pt-50 translate-x-[-50%]">
              <p
                className="text-16 text-black font-semibold"
                style={{ textAlign: "center", marginTop: "20px" }}
              >
                No transactions till now
              </p>
              <p className="text-14 text-black font-normal">
                Please click on '+' CTA to create a new transaction
              </p>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={transactionList.length}
              next={fetchMoreData}
              hasMore={hasMoreData}
              loader={getLoadMore()}
              height={
                isMobileDevice()
                  ? "calc( 100vh - 368px)"
                  : "calc( 100vh - 385px)"
              }
              endMessage={
                <p style={{ textAlign: "center", marginTop: "20px" }}>
                  <b>No more transactions</b>
                </p>
              }
            >
              {transactionList.map((obj: any, idx: number) => (
                <React.Fragment key={idx}>
                  {getTransactionRowView(obj)}
                </React.Fragment>
              ))}
            </InfiniteScroll>
          )}
        </div>
      </div>
      {showTransactionInfoModal && (
        <TransactionInfoModal
          onModalClose={closeInfoModal}
          transactionInfo={currentTransactionDetails}
          openEditTransaction={showEditTransaction}
          refreshTableData={refreshTableData}
          showEditBtnCheck={showEditBtn}
          branchName={selectedBranch.branchName}
        />
      )}
    </div>
  );
};

export default CashManagementTable;
